import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PluggyConnect } from 'react-pluggy-connect';
import FinanceService from '../services/FinanceService';

const safeMessage = (error, fallback) => {
  const message = error?.response?.data?.error;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : fallback;
};

const selectionAccounts = (error) => {
  if (error?.response?.status !== 409) return [];
  const accounts = error?.response?.data?.details?.accounts;
  if (!Array.isArray(accounts)) return [];
  return accounts
    .map((account) => ({
      id: typeof account?.id === 'string' ? account.id.trim() : '',
      displayName:
        typeof account?.displayName === 'string' && account.displayName.trim()
          ? account.displayName.trim()
          : 'Conta bancária',
      accountNumberMasked:
        typeof account?.accountNumberMasked === 'string'
          ? account.accountNumberMasked.trim()
          : '',
      currency:
        typeof account?.currency === 'string' && account.currency.trim()
          ? account.currency.trim()
          : 'BRL',
    }))
    .filter((account) => account.id);
};

const itemIdFromSuccess = (data) => {
  const itemId = data?.item?.id;
  return typeof itemId === 'string' ? itemId.trim() : '';
};

export default function OpenFinanceConnectButton({
  account,
  canManage,
  showAlert,
  onConnected,
  autoStart = false,
  onAutoStart,
  buttonLabel = 'Escolher banco e conectar',
  includeSandbox = process.env.REACT_APP_PLUGGY_INCLUDE_SANDBOX === 'true',
}) {
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [session, setSession] = useState(null);
  const [selection, setSelection] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [pluggyCredentials, setPluggyCredentials] = useState({ clientId: '', clientSecret: '' });
  const autoStartAttemptedRef = useRef(false);

  const accountId = Number(account?.id);
  const isEligible =
    canManage &&
    Number.isInteger(accountId) &&
    accountId > 0 &&
    account?.integrationType === 'API';

  const busy = starting || completing;

  const resetSession = useCallback(() => {
    setSession(null);
  }, []);

  const complete = useCallback(
    async (itemId, remoteAccountId) => {
      setCompleting(true);
      try {
        const payload = {
          itemId,
          ...(remoteAccountId ? { remoteAccountId } : {}),
        };
        const { data } = await FinanceService.completeOpenFinanceConnection(
          accountId,
          payload,
        );
        setSession(null);
        setSelection(null);
        showAlert?.(
          'success',
          'Banco conectado. A primeira sincronização foi solicitada.',
        );
        await onConnected?.(data);
      } catch (error) {
        const accounts = remoteAccountId ? [] : selectionAccounts(error);
        if (accounts.length > 0) {
          setSession(null);
          setSelection({ itemId, accounts });
          return;
        }
        showAlert?.(
          'error',
          safeMessage(
            error,
            'Não foi possível concluir a conexão bancária.',
          ),
        );
      } finally {
        setCompleting(false);
      }
    },
    [accountId, onConnected, showAlert],
  );

  const openGuide = useCallback(() => {
    if (!isEligible || busy) return;
    setSelection(null);
    setGuideOpen(true);
  }, [busy, isEligible]);

  const start = useCallback(async () => {
    if (!isEligible || busy) return;
    if (autoStart) onAutoStart?.();
    if (!pluggyCredentials.clientId.trim() || !pluggyCredentials.clientSecret.trim()) {
      showAlert?.('error', 'Informe o Client ID e o Client Secret da sua conta Pluggy.');
      return;
    }
    setGuideOpen(false);
    setStarting(true);
    setSelection(null);
    try {
      const { data } = await FinanceService.startOpenFinanceConnection(accountId, {
        clientId: pluggyCredentials.clientId.trim(),
        clientSecret: pluggyCredentials.clientSecret.trim(),
      });
      setPluggyCredentials((current) => ({ ...current, clientSecret: '' }));
      const connectToken =
        typeof data?.connectToken === 'string' ? data.connectToken.trim() : '';
      if (data?.mode !== 'CONNECT_TOKEN' || !connectToken) {
        throw new Error('INVALID_CONNECT_SESSION');
      }
      setSession({
        connectToken,
        expiresAt: data.expiresAt || null,
      });
    } catch (error) {
      setSession(null);
      showAlert?.(
        'error',
        safeMessage(
          error,
          'Não foi possível iniciar a conexão bancária.',
        ),
      );
    } finally {
      setStarting(false);
    }
  }, [accountId, autoStart, busy, isEligible, onAutoStart, pluggyCredentials, showAlert]);

  useEffect(() => {
    if (!autoStart) {
      autoStartAttemptedRef.current = false;
      return;
    }
    if (!isEligible || autoStartAttemptedRef.current) return;
    autoStartAttemptedRef.current = true;
    setGuideOpen(true);
  }, [autoStart, isEligible]);

  const handleSuccess = useCallback(
    async (data) => {
      const itemId = itemIdFromSuccess(data);
      if (!itemId) {
        setSession(null);
        showAlert?.(
          'error',
          'A tela segura não retornou uma conexão bancária válida.',
        );
        return;
      }
      await complete(itemId);
    },
    [complete, showAlert],
  );

  const handleProviderError = useCallback(() => {
    setSession(null);
    showAlert?.(
      'error',
      'A conexão bancária não foi concluída. Tente novamente.',
    );
  }, [showAlert]);

  const widget = useMemo(
    () =>
      session ? (
        <PluggyConnect
          connectToken={session.connectToken}
          includeSandbox={includeSandbox}
          language="pt"
          countries={['BR']}
          allowConnectInBackground={false}
          connectorSortAlphabetically
          onSuccess={handleSuccess}
          onError={handleProviderError}
          onLoadError={handleProviderError}
          onClose={resetSession}
        />
      ) : null,
    [
      handleProviderError,
      handleSuccess,
      includeSandbox,
      resetSession,
      session,
    ],
  );

  if (!isEligible) return null;

  return (
    <>
      <button
        type="button"
        onClick={openGuide}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-50"
      >
        {busy ? 'Conectando…' : buttonLabel}
      </button>

      {widget}

      {guideOpen && (
        <div
          role="dialog"
          aria-label="Escolha como conectar o banco"
          aria-modal="true"
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 p-4"
        >
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-semibold text-slate-900">
              Como você quer conectar o banco?
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Use sua própria conta Pluggy. A cobrança e o plano ficam vinculados
              diretamente a você, não ao Nash.
            </p>

            <div className="mt-5 grid gap-3">
              <label className="text-sm font-medium text-slate-700">
                Client ID da sua conta Pluggy
                <input
                  value={pluggyCredentials.clientId}
                  onChange={(event) => setPluggyCredentials((current) => ({ ...current, clientId: event.target.value }))}
                  autoComplete="off"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  required
                />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Client Secret da sua conta Pluggy
                <input
                  type="password"
                  value={pluggyCredentials.clientSecret}
                  onChange={(event) => setPluggyCredentials((current) => ({ ...current, clientSecret: event.target.value }))}
                  autoComplete="off"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  required
                />
              </label>
              <a href="https://dashboard.pluggy.ai/" target="_blank" rel="noreferrer" className="text-xs font-semibold text-sky-700 underline">
                Criar ou acessar minha conta Pluggy
              </a>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={start}
                className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 text-left hover:border-emerald-500 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              >
                <span className="inline-flex rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                  Recomendado
                </span>
                <span className="mt-3 block font-semibold text-slate-900">
                  Autorizar pelo Open Finance
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-600">
                  Você aprova o compartilhamento no app ou site oficial do banco,
                  quando essa opção estiver disponível.
                </span>
              </button>

              <button
                type="button"
                onClick={start}
                className="rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-sky-300 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-300"
              >
                <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-800">
                  Acesso alternativo
                </span>
                <span className="mt-3 block font-semibold text-slate-900">
                  Usar credenciais bancárias
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-600">
                  Para bancos com conector direto, informe login, senha e MFA
                  somente dentro da tela segura de conexão.
                </span>
              </button>
            </div>

            <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-3 text-xs leading-5 text-sky-900">
              <strong>Seus dados continuam protegidos.</strong> O Nash não cria
              campos de senha, não recebe e não armazena suas credenciais
              bancárias. O método disponível pode variar conforme a instituição.
            </div>

            <button
              type="button"
              onClick={() => setGuideOpen(false)}
              className="mt-4 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {selection && (
        <div
          role="dialog"
          aria-label="Selecione a conta bancária"
          aria-modal="true"
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 p-4"
        >
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-semibold text-slate-900">
              Selecione a conta bancária
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              O banco autorizou mais de uma conta. Escolha qual delas será
              vinculada a “{account.name}”.
            </p>
            <div className="mt-4 space-y-2">
              {selection.accounts.map((remoteAccount) => (
                <button
                  key={remoteAccount.id}
                  type="button"
                  disabled={completing}
                  onClick={() => complete(selection.itemId, remoteAccount.id)}
                  className="flex w-full items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-left hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50"
                >
                  <span>
                    <span className="block font-medium text-slate-800">
                      {remoteAccount.displayName}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {remoteAccount.accountNumberMasked || 'Número protegido'}
                    </span>
                  </span>
                  <span className="text-xs font-semibold text-slate-500">
                    {remoteAccount.currency}
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={completing}
              onClick={() => setSelection(null)}
              className="mt-4 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
